/**
 * App Navigation Hook — extracted from App.tsx
 *
 * Manages navigation state, view routing, cross-portal navigation,
 * and account switching (navigation side-effects).
 *
 * @moved from src/hooks/useAppNavigation.ts (B1 — DDD domain move to app/)
 */

import { useState, useEffect, useCallback } from 'react'
import { type AccountsState, type Account } from '../../accounts'

export type AppView = 'dashboard' | 'practice'

export type NavView =
  | 'home'
  | 'modules'
  | 'progress'
  | 'collection'
  | 'settings'
  | 'assessment'
  | 'assessmentReport'
  | 'parent'
  | 'teacher'
  | 'learningLab'
  | 'admin'
  | 'login'
  | 'register'

interface UseAppNavigationParams {
  accounts: AccountsState
  activeAccount: Account | null
  isInitialized: boolean
  switchAccount: (accountId: string) => void
  setViewingLearner: (learnerId: string | null) => void
  loadDemoAccounts: () => void
  clearActiveAccount: () => void
}
export function useAppNavigation({
  accounts,
  activeAccount,
  isInitialized,
  switchAccount,
  setViewingLearner,
  loadDemoAccounts,
  clearActiveAccount,
}: UseAppNavigationParams) {
  const [currentView, setCurrentView] = useState<AppView>('dashboard')
  const [currentNavView, setCurrentNavView] = useState<NavView>('home')
  const [previousNavView, setPreviousNavView] = useState<NavView | null>(null)
  const [showWelcome, setShowWelcome] = useState(false)

  // Demo-first: auto-load demo accounts when state is empty
  // Also: detect logged-out state (activeAccountId empty) and show WelcomeScreen
  useEffect(() => {
    if (isInitialized) {
      const accountsList = Object.values(accounts.accountsById)
      if (accountsList.length === 0) {
        // No accounts at all — auto-load demo
        loadDemoAccounts()
      } else if (!accounts.activeAccountId) {
        // Logged out but accounts exist — show WelcomeScreen
        // This fixes the limbo state after logout + page refresh
        setShowWelcome(true)
      }
    }
  }, [isInitialized]) // Only run on init, not on every accounts change

  // Account switching with navigation routing
  const handleSwitchAccount = useCallback(
    (accountId: string) => {
      switchAccount(accountId)
      setPreviousNavView(null)
      const newAccount = accounts.accountsById[accountId]
      if (newAccount) {
        switch (newAccount.role) {
          case 'learner':
            setCurrentNavView('home')
            break
          case 'parent':
            setCurrentNavView('parent')
            break
          case 'teacher':
            setCurrentNavView('teacher')
            break
          case 'admin':
            setCurrentNavView('admin')
            break
        }
      }
    },
    [switchAccount, accounts.accountsById],
  )

  const handleLoadDemoAccounts = useCallback(() => {
    loadDemoAccounts()
    setShowWelcome(false)
    setCurrentNavView('home')
  }, [loadDemoAccounts])

  // Cross-Portal Navigation
  const handleNavigateToLearner = useCallback(
    (learnerId: string) => {
      setViewingLearner(learnerId)
      setPreviousNavView(currentNavView)
      setCurrentNavView('home')
    },
    [setViewingLearner, currentNavView],
  )

  const handleReturnToPortal = useCallback(() => {
    setViewingLearner(null)
    if (previousNavView) {
      setCurrentNavView(previousNavView)
      setPreviousNavView(null)
    } else {
      if (activeAccount?.role === 'parent') setCurrentNavView('parent')
      else if (activeAccount?.role === 'teacher') setCurrentNavView('teacher')
      else if (activeAccount?.role === 'admin') setCurrentNavView('admin')
      else setCurrentNavView('home')
    }
  }, [setViewingLearner, previousNavView, activeAccount?.role])

  const handleLogout = useCallback(() => {
    clearActiveAccount()
    setViewingLearner(null)
    setShowWelcome(true)
    setCurrentNavView('home')
    // F4: If only demo accounts remain after logout, the next "Demo Verkennen"
    // will replace them via loadDemoAccounts(). No extra cleanup needed here
    // because clearActiveAccount just sets activeAccountId = '' and the
    // WelcomeScreen init effect (F1) handles the rest on page refresh.
  }, [clearActiveAccount, setViewingLearner])

  // Navigate to login screen (from welcome or test button)
  const handleNavigateToLogin = useCallback(() => {
    setShowWelcome(false)
    setCurrentNavView('login')
  }, [])

  // Navigate to register screen
  const handleNavigateToRegister = useCallback(() => {
    setShowWelcome(false)
    setCurrentNavView('register')
  }, [])

  // Handle successful login — route to correct portal based on role
  // Accepts optional role parameter to avoid stale-closure lookup after createAccount
  const handleLoginSuccess = useCallback(
    (accountId: string, role?: 'learner' | 'parent' | 'teacher' | 'admin') => {
      switchAccount(accountId)
      setShowWelcome(false)
      // Use provided role first (avoids stale state after registration),
      // fall back to state lookup for login flow
      const accountRole = role || accounts.accountsById[accountId]?.role
      switch (accountRole) {
        case 'learner':
          setCurrentNavView('home')
          break
        case 'parent':
          setCurrentNavView('parent')
          break
        case 'teacher':
          setCurrentNavView('teacher')
          break
        case 'admin':
          setCurrentNavView('admin')
          break
        default:
          setCurrentNavView('home')
      }
    },
    [switchAccount, accounts.accountsById],
  )

  // Reset to dashboard view (used after practice completes)
  const resetToDashboard = useCallback(() => {
    setCurrentView('dashboard')
  }, [])

  // Reset to learning lab view (used after mode sessions complete)
  const resetToLearningLab = useCallback(() => {
    setCurrentView('dashboard')
    setCurrentNavView('learningLab')
  }, [])

  const enterPracticeView = useCallback(() => {
    setCurrentView('practice')
  }, [])

  return {
    currentView,
    setCurrentView,
    currentNavView,
    setCurrentNavView,
    showWelcome,
    setShowWelcome,
    handleSwitchAccount,
    handleLoadDemoAccounts,
    handleNavigateToLearner,
    handleReturnToPortal,
    handleLogout,
    handleNavigateToLogin,
    handleNavigateToRegister,
    handleLoginSuccess,
    resetToDashboard,
    resetToLearningLab,
    enterPracticeView,
  }
}
